/**
 * `user_sync` renderer — invisible auto-advancing step that creates a
 * product-backend user record after authentication.
 *
 * Reads the display name from the answers bag (`data.nameAnswerKey`) and
 * fires `onAnswer` with the sync outcome. The actual HTTP calls are
 * delegated to the orchestrator via the `onAnswer` action contract so
 * survey-kit stays API-agnostic.
 *
 * Step JSON shape:
 *
 *   {
 *     "type": "user_sync",
 *     "data": {
 *       "answerKey": "user_sync",
 *       "nameAnswerKey": "display_name",
 *       "message": "Setting up your profile…"
 *     }
 *   }
 */
import { z } from "zod";

import { createRenderer } from "@survey-kit/registry";
import type { RendererProps } from "@survey-kit/types";

export const userSyncSchema = z
  .object({
    answerKey: z.string().min(1),
    /** Key in the answers bag that holds the display name (from a prior name_capture step). */
    nameAnswerKey: z.string().min(1),
    /** Optional spinner message shown while the sync runs. */
    message: z.string().optional(),
  })
  .passthrough();

export type UserSyncData = z.infer<typeof userSyncSchema>;

/**
 * The default renderer shows a spinner. The actual user creation is handled
 * by the orchestrator override in the funnel app, which replaces this with
 * a component that calls the product API.
 */
const UserSyncRenderer = ({ data }: RendererProps<UserSyncData>) => (
  <div className="flex flex-col items-center gap-3 p-6" role="status" aria-label="Syncing">
    <div className="size-8 animate-spin rounded-full border-2 border-white/20 border-t-white" />
    <p className="text-sm opacity-80">{data.message ?? "Setting up your profile\u2026"}</p>
  </div>
);

export const userSyncRenderer = createRenderer({
  type: "user_sync",
  dataSchema: userSyncSchema,
  label: "User sync",
  Component: UserSyncRenderer,
});

export default userSyncRenderer;
